<template>
  <div class="lab-objectives-container w-full flex mb-10">
    <h4 v-if="labType !== 'pta'" class="text-red">
      Lab Objectives available only for PTA Labs.
    </h4>
    <div v-else class="lab-objectives-list">
      <div
        class="lab-objective-item w-11/12 flex flex-wrap mb-10"
        v-for="localObjective of localObjectives"
        :key="localObjective.id"
      >
        <div
          class="lab-objective-item-open w-full flex flex-wrap mb-10"
          v-if="isOpen(localObjective.id)"
        >
          <label>Description md</label>
          <markdown-editor
            :id="localObjective.id"
            class="w-full objectives-item-descriptionMd-field mb-5"
            ref="markdownEditor"
            v-model="localObjective.description_md"
            :options="{
              hideModeSwitch: true,
              previewStyle: 'tab',
            }"
          />

          <label>Verification script</label>
          <MonacoEditor
            class="w-full monaco-editor mb-5"
            theme="vs-dark"
            height="300"
            width="600"
            language="python"
            v-model="localObjective.verification_script"
          />

          <div class="w-full flex justify-between">
            <vs-input
              class="namespace-field"
              label="Points"
              v-model.number="localObjective.points"
            />
            <div class="flex items-end">
              <vs-button
                class="mr-5 save-button"
                color="primary"
                type="filled"
                icon-pack="feather"
                @click.stop="saveObjective(localObjective)"
              >
                Save
              </vs-button>
              <vs-button
                class="cancel-button"
                color="primary"
                type="border"
                icon-pack="feather"
                @click.stop="closeAndDiscard(localObjective.id)"
              >
                Cancel
              </vs-button>
            </div>
          </div>
        </div>
        <div class="lab-objective-item-close w-full" v-else>
          <div class="flex justify-end">
            <div
              class="open-objective-btn w-full cursor-pointer p-4 border border-solid rounded border-grey-light"
              :style="{ fixed: true }"
              @click.stop="openObjective(localObjective.id)"
            >
              <prism-markdown
                :content="localObjective.description_md || 'empty description'"
              />
            </div>
            <div
              class="p-4 cursor-pointer delete-objective"
              @click.stop="deleteObjective(localObjective)"
              title="Delete objective"
            >
              <feather-icon
                icon="Trash2Icon"
                svgClasses="h-5 w-5 color-current text-danger"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        class="add-objective-btn flex text-lg mt-10 cursor-pointer"
        @click.stop="addObjective()"
      >
        <feather-icon icon="PlusCircleIcon" svgClasses="h-6 w-6" class="mr-4" />
        Add objective
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import cloneDeep from 'lodash.clonedeep'
import MarkdownEditor from '@/components/markdownEditor'
import PrismMarkdown from '@/components/PrismMarkup'
import MonacoEditor from 'monaco-editor-vue'
import { searchVideos } from '@/utils/mixins'
import uuid from 'uuid'

export default {
  mixins: [searchVideos],
  components: {
    MarkdownEditor,
    MonacoEditor,
    PrismMarkdown,
  },
  data() {
    return {
      openObjectiveIds: [],
      localObjectives: [],
    }
  },
  watch: {
    // Immediate because vs-tab only mounts this when its tab is opened, after
    // the parent has already fetched the objectives into the store.
    getObjectives: {
      handler(objectives) {
        this.localObjectives = cloneDeep(objectives)
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters('labs', ['getCurrentLab', 'getObjectives']),
    labType() {
      return this.getCurrentLab.lab_type
    },
  },
  methods: {
    ...mapActions('labs', [
      'resetCurrentLab',
      'fetchObjectives',
      'saveObjectiveToApi',
      'deleteObjectiveToApi',
    ]),
    async saveObjective(objective) {
      const payload = objective

      if (this.isNewObjective(objective.id)) delete payload.id

      try {
        this.$vs.loading()

        await this.saveObjectiveToApi(payload)

        this.closeObjective(objective.id)

        this.$vs.notify({
          title: 'Success',
          text: 'Objective saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })
      } catch (error) {
        this.showError(error)
      }
      this.$vs.loading.close()
    },
    async deleteObjective(objective) {
      this.$vs.loading()

      try {
        await this.deleteObjectiveToApi(objective)
      } catch (err) {
        this.showError(err)
      }

      this.$vs.loading.close()
    },
    closeObjective(objectiveId) {
      this.openObjectiveIds = this.openObjectiveIds.filter(
        (id) => id != objectiveId
      )
    },
    closeAndDiscard(objectiveId) {
      const oIndex = this.localObjectives.findIndex((o) => o.id == objectiveId)

      if (this.isNewObjective(objectiveId)) {
        this.localObjectives.splice(oIndex, 1)
      } else {
        const storedObjective = this.getObjectives.find(
          (o) => o.id == objectiveId
        )

        this.localObjectives.splice(oIndex, 1, storedObjective)
      }

      this.closeObjective(objectiveId)
    },
    openObjective(objectiveId) {
      this.openObjectiveIds.push(objectiveId)
    },
    isOpen(objectiveId) {
      return this.openObjectiveIds.find((id) => id == objectiveId)
    },
    isNewObjective(objectiveId) {
      return objectiveId.startsWith('tmpId')
    },
    addObjective() {
      const tmpId = `tmpId-${uuid.v4()}`
      this.localObjectives.push({ id: tmpId, lab: this.getCurrentLab.id })
      this.openObjective(tmpId)
    },
  },
}
</script>
